import React, { useContext, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { FlowOrchestratorContext } from '@storefront/app/flow-orchestrator/context';
import { FlagsContext } from '@storefront/context/LDFlagsContext';
import useProductsInformations from '@storefront/hooks/useProductInformation';
import { NamespacesI18n } from '@storefront/i18n';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';
import { getRegularPriceFromProduct } from '@storefront/utils/bundlePriceCalculator';
import {
  getPrimaryThumbnailURL,
  isBundleProductOutOfStock,
} from '@storefront/utils/prodRequests';

import ModalSelectionAnalytics from './modal-selection-analytics-hoc';
import { SelectionButton } from '../../selection-button/selection-button';
import { configurationState } from '../../state-management/pdp-config-state';
import {
  classifyPrinter,
  findProductOption,
  isBackOrderShipping,
  isPrinterPopular,
  saveProductConfiguration,
} from '../../utils/utils';
import styles from '../product-configuration-visid.module.scss';
import {
  getProductLevelImage,
  scrollToElement,
} from '../product-configuration.utils';
import ImageRenderer from '../render-image';
import { findBundleOption } from '@storefront/app/pdp-config/printer/utils/utils';

export type MainProductProps = {
  showSmallImage: boolean;
  loadingImage: boolean;
  setLoadingImage: (loading: boolean) => void;
  skuList: string[];
};
export default function MainProduct(props: Readonly<MainProductProps>) {
  const { showSmallImage, loadingImage, setLoadingImage, skuList } = props;
  const { t } = useTranslation(NamespacesI18n.PDP_CONFIG);
  const [hasScrolled, setHasScrolled] = useState(false);
  const { enableBackOrder, enableDescriptionFromBundle } =
    useContext(FlagsContext);
  const { isSMB } = useContext(FlowOrchestratorContext);

  const {
    configuration,
    activeSKU,
    bundleProduct,
    totalSteps,
    multipleProduct,
  } = useProductsInformations(skuList);

  const productLevelImage = getProductLevelImage(
    activeSKU,
    bundleProduct,
    showSmallImage,
    isSMB
  );
  const modal = 'printerComparison';
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newState = {
      ...configuration,
      activeSku: event.target.value,
    };
    setLoadingImage(true);
    configurationState(newState);
    saveProductConfiguration(newState);
    if (!hasScrolled) {
      scrollToElement('required-options-container');
      setHasScrolled(true);
    }
  };

  return (
    <div className={styles['main-product-container']}>
      {!showSmallImage && (
        <ImageRenderer
          src={productLevelImage}
          title={bundleProduct?.name}
          id={'bundleProductImage'}
          loadingImage={loadingImage}
          setLoadingImage={setLoadingImage}
          sku={bundleProduct?.sku}
          nonSelectedPrinter={!activeSKU}
        />
      )}
      <div className={styles['options']} data-testid="main-product-options">
        <div className={styles['label-container']}>
          <span
            className={classNames(styles['description'], 'caption-regular')}
          >
            {t('radioButtonGroup.counter', { ith: 1, total: totalSteps })}{' '}
          </span>
        </div>
        <span className={classNames(styles['title'], 'subtitle-large')}>
          {t('pdpConfig.printInkBundle.bundleSelectionTitle')}
        </span>
        {showSmallImage && (
          <ImageRenderer
            src={productLevelImage}
            title={bundleProduct?.name}
            id={'ProductImage'}
            loadingImage={loadingImage}
            setLoadingImage={setLoadingImage}
            sku={bundleProduct?.sku}
          />
        )}
        <div className={styles['options-column-container']}>
          {skuList?.map((sku) => {
            const product = multipleProduct.find(
              (product) => product.sku === sku
            );

            const coreOffer = findProductOption(sku, multipleProduct)?.product;
            const bundleProduct = findBundleOption(sku, multipleProduct);
            const description = enableDescriptionFromBundle
              ? bundleProduct.short_description.html
              : coreOffer.short_description.html;

            return (
              <SelectionButton
                key={sku}
                inputTestId={sku}
                checked={activeSKU === sku}
                title={classifyPrinter(product)}
                name={coreOffer.hp_short_config_name}
                onChange={handleChange}
                value={sku}
                popular={isPrinterPopular(multipleProduct, sku)}
                backOrderShipping={isBackOrderShipping(enableBackOrder, sku)}
                id={sku}
                description={description}
                price={getRegularPriceFromProduct(product)}
                image={getPrimaryThumbnailURL(product)}
                outOfStock={isBundleProductOutOfStock(product)}
                mainOffer
              />
            );
          })}
        </div>
        <ModalSelectionAnalytics
          modalName={modal}
          simpleUiEvent={uiEvents.printerConfigurationPageChoosePrinterSD}
          elementId="printer-modal-container"
          treshold={0.1}
        />
      </div>
    </div>
  );
}
